import React from "react";
import { motion } from "framer-motion";

import kvicOverview from "./images1/kvic/04-kvic-plant-installation-2.jpg";
import kvicFlow from "./images1/kvic/02-kvic-biogas-process-flow.png";
import kvicPlant1 from "./images1/kvic/03-kvic-plant-installation-1.png";
import kvicPlant2 from "./images1/kvic/01-kvic-model-overview.png";

import deenbandhuDiagram from "./images1/kvic/05-deenbandhu-model-diagram.png";
import deenbandhuPlant from "./images1/kvic/06-deenbandhu-model-plant.png";

import balloonDiagram from "./images1/kvic/07-balloon-type-model-diagram.png";
import balloonPlant from "./images1/kvic/08-balloon-type-plant.png";

const KVICBiogas = () => {
  const feedstocks = [
    "Cattle dung",
    "Kitchen and food waste",
    "Agricultural organic waste",
    "Farm and livestock waste",
    "Other suitable biodegradable organic materials",
  ];

  return (
    <div className="bg-white">

      {/* =========================================================
    1. OVERVIEW - BIO GAS PLANTS
========================================================= */}

<section className="bg-emerald-950 px-6 pt-28 pb-10">
  <div className="max-w-7xl mx-auto">

    <motion.div
      initial={{ opacity: 0, x: -30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6 }}
    >

      <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
        Overview - Bio Gas Plants
      </h2>

      <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
        KVIC MODEL BIOGAS PLANTS
      </h1>

      <h2 className="text-xl md:text-2xl text-amber-400 leading-relaxed">
        Turning Cow/ Organic matter Dung into Clean Energy,
        Organic Fertilizer & Rural Value
      </h2>

    </motion.div>

  </div>
</section>


      {/* =========================================================
          2. INTRODUCTION
      ========================================================= */}

      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-center">

          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >

            <p className="text-gray-700 leading-relaxed mb-6">
              The KVIC (Khadi and Village Industries Commission) model is a
              well-established biogas technology recognised for its practical
              design, simple operation and effective gas-storage arrangement.
              It is particularly suited to rural, agricultural, household,
              dairy, institutional and small-scale applications where locally
              available organic waste can be converted into biogas for
              productive energy use.
            </p>

            <p className="text-gray-700 leading-relaxed">
              Our approach goes beyond the digester itself. We engineer the
              complete system around the feedstock characteristics, quantity of
              organic waste available, required gas production, site conditions
              and intended end use, enabling clients to obtain a practical and
              application-oriented biogas solution.
            </p>

          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gray-100 rounded-2xl p-4 shadow-lg"
          >

            <img
              src={kvicOverview}
              alt="KVIC Model Biogas Plant"
              className="w-full h-[380px] object-contain rounded-xl"
            />

          </motion.div>

        </div>
      </section>


      {/* =========================================================
          3. OUR KVIC BIOGAS PLANT SOLUTIONS
      ========================================================= */}

      <section className="bg-emerald-50 py-14 px-6">
        <div className="max-w-7xl mx-auto">

          <h2 className="text-4xl font-bold text-emerald-950 mb-5">
            Our KVIC Biogas Plant Solutions
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            A KVIC biogas plant provides a simple pathway for transforming
            biodegradable organic materials into renewable biogas through
            anaerobic digestion.
          </p>

          <h3 className="text-2xl font-bold text-emerald-900 mb-4">
            Typical feedstocks may include:
          </h3>

          <div className="space-y-3 mb-6">

            {feedstocks.map((item, index) => (
              <div
                key={index}
                className="flex items-start gap-3"
              >
                <span className="text-amber-500 font-bold text-xl">
                  •
                </span>

                <p className="text-gray-700 leading-relaxed">
                  {item}
                </p>
              </div>
            ))}

          </div>

          <p className="text-gray-700 leading-relaxed">
            The system can be configured to support applications ranging from
            household cooking to institutional and agricultural energy
            requirements, depending on plant capacity and feedstock
            availability.
          </p>

        </div>
      </section>


      {/* =========================================================
          4. BASIC PROCESS
      ========================================================= */}

      <section className="py-14 px-6">
        <div className="max-w-7xl mx-auto">

          <div className="grid md:grid-cols-2 gap-10 items-center">

            <div className="h-[380px] rounded-xl overflow-hidden shadow-lg bg-gray-100 p-4">

              <img
                src={kvicFlow}
                alt="KVIC Basic Process"
                className="w-full h-full object-contain"
              />

            </div>

            <div>

              <h2 className="text-4xl font-bold text-emerald-950 mb-5">
                Basic Process
              </h2>

              <p className="text-gray-700 leading-relaxed">
                The KVIC biogas process converts biodegradable organic waste
                into renewable biogas through anaerobic digestion.
              </p>

              <p className="text-gray-700 leading-relaxed mt-5">
                This creates a circular resource-recovery system in which
                organic waste is converted into both renewable energy and a
                useful by-product.
              </p>

            </div>

          </div>

        </div>
      </section>


      {/* =========================================================
          5. ENGINEERING OF COMPLETE BIOGAS SYSTEMS
      ========================================================= */}

      <section className="bg-emerald-50 py-14 px-6">
        <div className="max-w-7xl mx-auto">

          <h2 className="text-4xl font-bold text-emerald-950 mb-5">
            Engineering of Complete Biogas Systems
          </h2>

          <p className="text-gray-700 leading-relaxed mb-8">
            RENVITEK Engineers LLP can support the development of the complete
            biogas system rather than treating the digester as an isolated
            component.
          </p>


          {/* 1. FEEDSTOCK ASSESSMENT */}

          <div className="mb-8">

            <h3 className="text-2xl font-bold text-emerald-900 mb-4">
              1. Feedstock Assessment
            </h3>

            <p className="text-gray-700 leading-relaxed mb-4">
              We consider the type, quantity and availability of organic
              feedstock to determine the appropriate plant configuration and
              capacity.
            </p>

            <p className="text-gray-700 leading-relaxed">
              The selection of the system is based on the daily feed
              availability, characteristics of the feedstock, required biogas
              output and intended application.
            </p>

          </div>


          {/* 2. FEED PREPARATION */}

          <div className="mb-8">

            <h3 className="text-2xl font-bold text-emerald-900 mb-4">
              2. Feed Preparation & Pre-treatment
            </h3>

            <p className="text-gray-700 leading-relaxed mb-4">
              Organic waste may require appropriate preparation before entering
              the digester. Depending on the feedstock, this may include:
            </p>

            <ul className="list-disc pl-8 space-y-2 text-gray-700 leading-relaxed mb-4">
              <li>Size reduction</li>
              <li>Mixing</li>
              <li>Slurry preparation</li>
              <li>
                Dilution with suitable process water/return slurry
              </li>
              <li>Preliminary conditioning</li>
            </ul>

            <p className="text-gray-700 leading-relaxed">
              Proper feed preparation supports consistent digester operation
              and efficient biological conversion.
            </p>

          </div>


          {/* 3. KVIC MODEL */}

          <div className="mb-8">

            <h3 className="text-2xl font-bold text-emerald-900 mb-4">
              3. KVIC Model Anaerobic Digestion
            </h3>

            <p className="text-gray-700 leading-relaxed">
              The KVIC model provides a practical and established
              configuration for anaerobic digestion, particularly for smaller
              decentralised applications. RENVITEK&apos;s engineering approach
              focuses on selecting and configuring the plant according to the
              project requirement rather than applying a single standard
              configuration to every site.
            </p>

          </div>


          {/* 4. GAS MANAGEMENT */}

          <div className="mb-8">

            <h3 className="text-2xl font-bold text-emerald-900 mb-4">
              4. Biogas Collection & Gas Management
            </h3>

            <p className="text-gray-700 leading-relaxed mb-4">
              The generated biogas is collected from the digester and conveyed
              through an appropriate gas pipeline system.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              Where required, the gas system can incorporate:
            </p>

            <p className="text-xl font-semibold text-emerald-900 leading-relaxed mb-4">
              Moisture Trap → H₂S Scrubbing → Biogas Booster → End Use
            </p>

            <p className="text-gray-700 leading-relaxed">
              This helps improve the quality and usability of biogas for
              downstream applications.
            </p>

          </div>


          {/* 5. GAS CLEANING */}

          <div className="mb-8">

            <h3 className="text-2xl font-bold text-emerald-900 mb-4">
              5. Gas Cleaning
            </h3>

            <p className="text-gray-700 leading-relaxed mb-4">
              Raw biogas generally contains moisture and hydrogen sulphide
              along with methane and carbon dioxide. Appropriate gas-
              conditioning systems can therefore be incorporated depending on
              the application.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              Our system configurations may include:
            </p>

            <ul className="list-disc pl-8 space-y-2 text-gray-700 leading-relaxed mb-4">
              <li>Moisture traps</li>
              <li>H₂S scrubbers</li>
              <li>Gas filtration/conditioning</li>
              <li>Pressure boosting arrangements</li>
            </ul>

            <p className="text-gray-700 leading-relaxed">
              The level of gas treatment is selected according to the intended
              application and operating requirements.
            </p>

          </div>


          {/* 6. BIOGAS BOOSTER */}

          <div className="mb-8">

            <h3 className="text-2xl font-bold text-emerald-900 mb-4">
              6. Biogas Booster Systems
            </h3>

            <p className="text-gray-700 leading-relaxed mb-4">
              For applications where additional gas pressure is required, a
              biogas booster can be incorporated into the gas-utilisation
              system.
            </p>

            <p className="text-gray-700 leading-relaxed">
              This is particularly useful for applications where the available
              digester gas pressure is insufficient for reliable delivery to
              the end-use equipment.
            </p>

          </div>


          {/* 7. DIGESTATE MANAGEMENT */}

          <div>

            <h3 className="text-2xl font-bold text-emerald-900 mb-4">
              7. Digestate Management
            </h3>

            <p className="text-gray-700 leading-relaxed mb-4">
              Anaerobic digestion does not merely produce biogas. The process
              also generates digested slurry.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              RENVITEK can incorporate appropriate digestate collection,
              storage and reuse arrangements, allowing the material to be
              utilised, subject to suitability and applicable practices, for:
            </p>

            <ul className="list-disc pl-8 space-y-2 text-gray-700 leading-relaxed mb-4">
              <li>Gardening</li>
              <li>Agriculture</li>
              <li>Soil and nutrient management</li>
              <li>Other beneficial organic applications</li>
            </ul>

            <p className="text-gray-700 leading-relaxed">
              This helps establish a waste-to-energy-and-nutrient recovery
              cycle.
            </p>

          </div>

        </div>
      </section>


      {/* =========================================================
          6. OUR SOLUTION RANGE
      ========================================================= */}

      <section className="py-14 px-6">
        <div className="max-w-7xl mx-auto">

          <h2 className="text-4xl font-bold text-emerald-950 mb-10">
            Our Solution Range:
          </h2>

          <div className="overflow-x-auto">

            <table className="w-full border-collapse border border-gray-300">

              <thead className="bg-emerald-950 text-white">

                <tr>

                  <th className="border border-gray-300 p-4 text-left">
                    Application
                  </th>

                  <th className="border border-gray-300 p-4 text-left">
                    Typical Capacity
                  </th>

                  <th className="border border-gray-300 p-4 text-left">
                    Recommended Application
                  </th>

                </tr>

              </thead>

              <tbody>

                <tr>
                  <td className="border border-gray-300 p-4 text-gray-700">
                    Households & Small Farms
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    2–6 m³
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    Cooking gas and LPG substitution
                  </td>
                </tr>

                <tr>
                  <td className="border border-gray-300 p-4 text-gray-700">
                    Institutions, Gaushalas & Dairy Clusters
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    10–25 m³
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    Cooking, canteen applications and hot-water systems
                  </td>
                </tr>

                <tr>
                  <td className="border border-gray-300 p-4 text-gray-700">
                    Commercial & Industrial
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    &gt;25 m³
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    Dairies, food processors, FPOs and larger cattle-waste
                    projects
                  </td>
                </tr>

                <tr>
                  <td className="border border-gray-300 p-4 text-gray-700">
                    Advanced Energy Applications
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    &gt;25 m³
                  </td>

                  <td className="border border-gray-300 p-4 text-gray-700">
                    Thermal energy, steam/hot water, small power generation
                    and, where feasible, biogas purification/CBG applications
                  </td>
                </tr>

              </tbody>

            </table>

          </div>

        </div>
      </section>


      {/* =========================================================
          7. ENGINEERING NOTE
      ========================================================= */}

      <section className="bg-emerald-50 py-12 px-6">
        <div className="max-w-6xl mx-auto">

          <div className="border-l-4 border-amber-400 bg-white p-7 rounded-xl shadow-lg">

            <p className="text-gray-700 leading-relaxed">

              <strong>
                Engineering Note:
              </strong>{" "}

              “KVIC” technically refers to the recognised floating
              gas-holder/floating-drum design. The underground or above-ground
              placement of the digester can vary according to the selected
              project configuration. RENVITEK can evaluate the site and
              recommend the most appropriate configuration.

            </p>

          </div>

        </div>
      </section>


      {/* =========================================================
          8. KVIC PLANT IMAGES
      ========================================================= */}

      <section className="py-14 px-6">
        <div className="max-w-7xl mx-auto">

          <div className="grid md:grid-cols-2 gap-8">

            <div className="bg-gray-100 rounded-xl p-4 shadow-lg h-[380px]">

              <img
                src={kvicPlant1}
                alt="KVIC Biogas Plant"
                className="w-full h-full object-contain"
              />

            </div>

            <div className="bg-gray-100 rounded-xl p-4 shadow-lg h-[380px]">

              <img
                src={kvicPlant2}
                alt="KVIC Model Biogas Plant"
                className="w-full h-full object-contain"
              />

            </div>

          </div>

        </div>
      </section>


      {/* =========================================================
          9. DEENBANDHU MODEL
      ========================================================= */}

      <section className="bg-emerald-50 py-14 px-6">
        <div className="max-w-7xl mx-auto">

          <h2 className="text-4xl font-bold text-emerald-950 mb-5">
            Deenbandhu Model
          </h2>

          <p className="text-gray-700 leading-relaxed">
            The Deenbandhu model is a low-cost, fixed-dome biogas plant that is
            popular in rural areas because it uses relatively less construction
            material and is economical to build. It is particularly suitable
            for small farms and household-level applications.
          </p>

          <div className="grid md:grid-cols-2 gap-8 mt-7">

            <div className="bg-white rounded-xl p-4 shadow-lg h-[380px]">

              <img
                src={deenbandhuDiagram}
                alt="Deenbandhu Model Diagram"
                className="w-full h-full object-contain"
              />

            </div>

            <div className="bg-white rounded-xl p-4 shadow-lg h-[380px]">

              <img
                src={deenbandhuPlant}
                alt="Deenbandhu Model Plant"
                className="w-full h-full object-contain"
              />

            </div>

          </div>

        </div>
      </section>


      {/* =========================================================
          10. BALLOON-TYPE BIOGAS PLANT
      ========================================================= */}

      <section className="py-14 px-6">
        <div className="max-w-7xl mx-auto">

          <h2 className="text-4xl font-bold text-emerald-950 mb-5">
            Balloon-Type Biogas Plant
          </h2>

          <p className="text-gray-700 leading-relaxed">
            Balloon-type plants are flexible and portable systems with
            comparatively lower gas pressure. Their simple installation and
            lower initial infrastructure requirements make them suitable for
            small households, farms and temporary or decentralized
            applications. For household and farm cooking, biogas is one of the
            most effective applications because it can directly substitute
            conventional fuels such as firewood, kerosene and LPG. Biogas can
            also be used for electricity generation through suitable
            biogas-generator systems.
          </p>

          <div className="grid md:grid-cols-2 gap-8 mt-7">

            <div className="bg-gray-100 rounded-xl p-4 shadow-lg h-[380px]">

              <img
                src={balloonDiagram}
                alt="Balloon-Type Biogas Model Diagram"
                className="w-full h-full object-contain"
              />

            </div>

            <div className="bg-gray-100 rounded-xl p-4 shadow-lg h-[380px]">

              <img
                src={balloonPlant}
                alt="Balloon-Type Biogas Plant"
                className="w-full h-full object-contain"
              />

            </div>

          </div>

        </div>
      </section>

    </div>
  );
};

export default KVICBiogas;